import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useReturnViewState, useRestoreScroll, PURCHASES_FLOW_PREFIXES, ACCEPTED_PURCHASES_VIEW_KEY } from '../hooks/useReturnViewState';
import Footer from '../components/Footer';
import { useNotifications } from '../contexts/NotificationContext';
import { placeholderFor } from '../utils/listingPlaceholder';

const getOverallPurchaseStatus = (purchase) => {
  const paymentStatus = purchase.paymentStatus || {};
  const shippingStatus = purchase.shippingStatus || {};
  
  // Check overall progress through the purchase flow
  const buyerPaid = paymentStatus.buyerPaid || false;
  const sellerPaid = paymentStatus.sellerPaid || false;
  const confirmationPhoto = shippingStatus.confirmationPhoto || false;
  const labelCreated = shippingStatus.labelCreated || false;
  const trackingNumber = shippingStatus.trackingNumber || false; // Check for tracking number
  const inTransit = shippingStatus.inTransit || false;
  const delivered = shippingStatus.delivered || false;
  
  // Determine overall status based on the furthest completed step
  if (delivered) {
    return 'Completed';
  } else if (inTransit) {
    return 'In Transit';
  } else if (trackingNumber) {
    // If there's a tracking number, package is at least in pre-transit
    return 'In Pre-Transit';
  } else if (labelCreated) {
    return 'Ready to Ship';
  } else if (confirmationPhoto) {
    return 'Awaiting Shipping Label';
  } else if (buyerPaid && sellerPaid) {
    return 'Awaiting Confirmation Photo';
  } else if (buyerPaid || sellerPaid) {
    return 'Payment In Progress';
  } else {
    return 'Payment Pending';
  }
};

const getUserPurchaseStep = (purchase, currentUserId) => {
  const isBuyer = purchase.initiatorId === currentUserId;
  const shipping = purchase.shippingStatus || {};
  
  // For purchase offers, only the seller has shipping steps
  const userShipping = isBuyer ? null : shipping.seller;
  
  const user = {
    paid: isBuyer ? (purchase.paymentStatus?.buyerPaid || false) : (purchase.paymentStatus?.sellerPaid || false),
    photoUploaded: isBuyer ? false : (shipping.confirmationPhoto ? true : false),
    ready: isBuyer ? false : shipping.sellerReady,
    label: isBuyer ? false : (shipping.labelCreated || false),
    inTransit: shipping.inTransit || false,
    delivered: shipping.delivered || false,
  };

  const steps = [
    { label: 'Completed Payment', check: (u) => u.paid },
    { label: 'Upload Confirmation Photo', check: (u) => u.photoUploaded },
    { label: 'Ready to Ship', check: (u) => u.ready },
    { label: 'Generate Shipping Label', check: (u) => u.label },
    // In Transit completes on delivery; the separate delivery step was folded
    // into it on the progress pages (2026-11-01). Same ladder here.
    { label: 'Item In Transit', check: (u) => u.delivered },
    { label: 'Feedback Provided', check: () => false }, // Placeholder
  ];

  // For buyers, only show payment step
  if (isBuyer) {
    return user.paid ? 'Payment Completed' : 'Payment Pending';
  }

  // For sellers, show their progress
  const completedIndex = steps.findIndex((step) => !step.check(user));
  return completedIndex === -1 ? 'Feedback Provided' : steps[completedIndex].label;
};

const AcceptedPurchases = () => {
  console.log('🔍 AcceptedPurchases component mounted');
  const { notifications, markAsRead } = useNotifications();
  const [acceptedPurchases, setAcceptedPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  // "Put me back where I was": capture-handler save + anchored restore, same
  // rationale as AcceptedTrades (see hooks/useReturnViewState.js).
  const { scrollSnapshot, saveView } = useReturnViewState({
    storageKey: ACCEPTED_PURCHASES_VIEW_KEY,
    flowPrefixes: PURCHASES_FLOW_PREFIXES,
  });
  useRestoreScroll(scrollSnapshot, !loading);
  const [currentUser, setCurrentUser] = useState(null);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [usernames, setUsernames] = useState({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth <= 768) return false;
    return window.scrollY > 400;
  });
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setShowBackToTop(y > 300);
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      } else {
        setIsHeaderCompact(y > 400);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, notLoggedIn]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const headerLinkBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    border: '1px solid #C89F3C',
    backgroundColor: 'white',
    color: '#374151',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '0.875rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  const renderMobileChromeBar = () => (
    <div className="ap-compact-bar-inner">
      <span className="ap-compact-title">Accepted Purchases</span>
      <span className="ap-compact-spacer" aria-hidden="true" />
      <Link to="/completed-purchases" className="ap-link-btn" style={headerLinkBtnStyle}>
        Completed Purchases
      </Link>
    </div>
  );

  // Mark purchase-related notifications as read when viewing accepted purchases
  useEffect(() => {
    const markPurchaseNotificationsRead = async () => {
      const purchaseNotifications = notifications.filter(
        n => ((n.type === 'purchase_offer_status_change' && n.metadata?.action === 'accepted') || 
              n.type === 'tracking_added_purchase') && 
             !n.read
      );
      
      if (purchaseNotifications.length > 0) {
        console.log(`📧 Marking ${purchaseNotifications.length} purchase notification(s) as read on AcceptedPurchases page`);
        for (const notification of purchaseNotifications) {
          await markAsRead(notification.id);
        }
      }
    };

    if (notifications.length > 0) {
      markPurchaseNotificationsRead();
    }
  }, [notifications, markAsRead]);
  const [userProfiles, setUserProfiles] = useState({});
  const [collapsedSteps, setCollapsedSteps] = useState({});

  const toggleStepCollapse = (purchaseId, stepIndex) => {
    setCollapsedSteps(prev => ({
      ...prev,
      [`${purchaseId}-${stepIndex}`]: !prev[`${purchaseId}-${stepIndex}`]
    }));
  };

  const getMembershipBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            9V Member
          </div>
        );
      case 'premium_plus':
        return (
          <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            18V Member
          </div>
        );
      default:
        return (
          <div className="flex items-center bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            Free Member
          </div>
        );
    }
  };

  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#7c3aed',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  const requestedUserIdsRef = useRef(new Set());
  const fetchUsernames = async (purchasesList) => {
    // Get user IDs from purchase offers
    const initiatorIds = purchasesList.map(p => p.initiatorId).filter(Boolean);
    const responderIds = purchasesList.map(p => p.responderId).filter(Boolean);
    const uniqueUserIds = [...new Set([...initiatorIds, ...responderIds])];
    const newUsernames = {};
    const newUserProfiles = {};

    // Parallel, and each profile fetched once per page life: same treatment
    // as the Offers hub (8f972cf). This page runs multiple live listeners,
    // and the serial loop re-fetched every profile on every fire.
    const toFetch = uniqueUserIds.filter((uid) => !requestedUserIdsRef.current.has(uid));
    if (toFetch.length === 0) return;
    toFetch.forEach((uid) => requestedUserIdsRef.current.add(uid));

    await Promise.all(toFetch.map(async (userId) => {
      try {
        const userSnap = await getDoc(doc(db, 'publicProfiles', userId));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          newUsernames[userId] = userData.username || 'Unknown User';
          newUserProfiles[userId] = {
            username: userData.username || 'Unknown User',
            membershipTier: userData.membershipTier || 'free',
            photoURL: userData.photoURL || '/images/default-avatar.png',
            badge: userData.badge || null,
            averageRating: userData.averageRating || 0
          };
        }
      } catch (error) {
        console.error('Error fetching username for user:', userId, error);
        requestedUserIdsRef.current.delete(userId); // let a later update retry
        newUsernames[userId] = 'Unknown User';
        newUserProfiles[userId] = {
          username: 'Unknown User',
          membershipTier: 'free',
          photoURL: '/images/default-avatar.png',
          badge: null,
          averageRating: 0
        };
      }
    }));

    console.log('🔍 AcceptedPurchases - Setting usernames:', newUsernames);
    console.log('🔍 AcceptedPurchases - Setting user profiles:', newUserProfiles);
    setUsernames((prev) => ({...prev, ...newUsernames}));
    setUserProfiles((prev) => ({...prev, ...newUserProfiles}));
  };

  useEffect(() => {
    let unsubscribeInitiator;
    let unsubscribeResponder;
    let unsubscribeAuth;
    let debugUnsubscribe;

    unsubscribeAuth = auth.onAuthStateChanged((user) => {
      console.log('🔍 AcceptedPurchases - Auth state changed:', !!user, user?.uid);
      setCurrentUser(user);
      if (!user) { setNotLoggedIn(true); setLoading(false); return; }

      const userId = user.uid;

      // Query for accepted purchase offers where user is buyer (initiator)
      const initiatorQuery = query(
        collection(db, 'purchaseOffers'),
        where('status', 'in', ['accepted', 'inProgress', 'payment_completed', 'paid']),
        where('initiatorId', '==', userId)
      );
      
      // Query for accepted purchase offers where user is seller (responder)
      const responderQuery = query(
        collection(db, 'purchaseOffers'),
        where('status', 'in', ['accepted', 'inProgress', 'payment_completed', 'paid']),
        where('responderId', '==', userId)
      );

      console.log('🔍 AcceptedPurchases - Querying for user:', userId);
      console.log('🔍 AcceptedPurchases - Status filter:', ['accepted', 'inProgress', 'payment_completed', 'paid']);

      // Debug: Query ALL purchases for this user to see what statuses exist
      const debugQuery = query(
        collection(db, 'purchaseOffers'),
        where('initiatorId', '==', userId)
      );
      
      const debugUnsubscribe = onSnapshot(debugQuery, (debugSnapshot) => {
        console.log('🔍 DEBUG - All purchases for user (as initiator):', debugSnapshot.docs.length);
        debugSnapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log('🔍 DEBUG - Purchase:', { id: doc.id, status: data.status, initiatorId: data.initiatorId });
        });
      });

      // Listen to both queries and combine results
      unsubscribeInitiator = onSnapshot(initiatorQuery, async (initiatorSnapshot) => {
        unsubscribeResponder = onSnapshot(responderQuery, async (responderSnapshot) => {
          console.log('🔍 AcceptedPurchases - Initiator purchases:', initiatorSnapshot.docs.length);
          console.log('🔍 AcceptedPurchases - Responder purchases:', responderSnapshot.docs.length);
          
          // Combine both snapshots
          const allDocs = [...initiatorSnapshot.docs, ...responderSnapshot.docs];
          console.log('🔍 AcceptedPurchases - Total purchases:', allDocs.length);
        
          const purchases = await Promise.all(allDocs.map(async docSnap => {
            const purchase = { id: docSnap.id, ...docSnap.data() };
            console.log('🔍 AcceptedPurchases - Processing purchase:', { 
              id: purchase.id, 
              status: purchase.status,
              initiatorId: purchase.initiatorId,
              responderId: purchase.responderId,
              targetPedalId: purchase.targetPedalId
            });

            // Get the target pedal (the one being sold)
            const targetPedalSnap = await getDoc(doc(db, 'listings', purchase.targetPedalId));
            const targetPedal = targetPedalSnap.exists() ? targetPedalSnap.data() : {};

            console.log('🔍 AcceptedPurchases - Pedal data for purchase:', {
              id: purchase.id,
              currentUserId: user?.uid,
              initiatorId: purchase.initiatorId,
              responderId: purchase.responderId,
              isBuyer: user?.uid === purchase.initiatorId,
              targetPedalId: purchase.targetPedalId,
              targetPedalExists: targetPedalSnap.exists(),
              targetPedalTitle: targetPedal?.title || 'No title'
            });

            return { ...purchase, targetPedal };
          }));

          const userId = auth.currentUser?.uid;
          console.log('🔍 AcceptedPurchases - All purchases:', purchases.map(p => ({ id: p.id, status: p.status, initiatorId: p.initiatorId, responderId: p.responderId })));
          console.log('🔍 AcceptedPurchases - Current user ID:', userId);

          // Filter purchases where user is involved
          const results = purchases.filter(purchase => 
            purchase.initiatorId === userId || purchase.responderId === userId
          );

          // Sort by acceptance date (from history), fallback to createdAt
          results.sort((a, b) => {
            // Find the acceptance timestamp from history
            const getAcceptanceDate = (purchase) => {
              if (purchase.history && Array.isArray(purchase.history)) {
                const acceptedEntry = [...purchase.history].reverse().find(h => 
                  h.message && h.message.toLowerCase().includes('accepted')
                );
                if (acceptedEntry && acceptedEntry.timestamp) {
                  return acceptedEntry.timestamp.toDate?.() || new Date(acceptedEntry.timestamp);
                }
              }
              return purchase.createdAt?.toDate?.() || new Date(0);
            };
            
            const aDate = getAcceptanceDate(a);
            const bDate = getAcceptanceDate(b);
            return bDate - aDate;
          });

          console.log('✅ Matching accepted purchases with pedal data:', results);
          setAcceptedPurchases(results);
          setLoading(false);
          
          // Fetch usernames for all users in purchases
          if (results.length > 0) {
            fetchUsernames(results);
          }
        });
      });
    });

    return () => {
      if (unsubscribeInitiator) unsubscribeInitiator();
      if (unsubscribeResponder) unsubscribeResponder();
      if (unsubscribeAuth) unsubscribeAuth();
      if (debugUnsubscribe) debugUnsubscribe();
    };
  }, []);

  if (notLoggedIn) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '500px', width: '100%', backgroundColor: '#e8f4fd', border: '2px solid #5A9ECB', borderRadius: '12px', padding: '2rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a5276', marginBottom: '1rem' }}>Log In to View Accepted Purchases</h2>
          <p style={{ fontSize: '1rem', color: '#2c3e50', lineHeight: '1.6', marginBottom: '1.5rem' }}>You need to be logged in to view your accepted purchases.</p>
          <Link to="/login" style={{ display: 'inline-block', backgroundColor: '#5A9ECB', color: 'white', fontWeight: '600', padding: '0.75rem 2rem', border: 'none', borderRadius: '6px', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-4">Loading accepted purchases...</div>;

  return (
    <div className="min-h-screen" onClickCapture={() => saveView('list')} style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Yellow background section */}
      <div
        className="accepted-purchases-page"
        style={{
          backgroundColor: '#F7F3EB',
          flex: 1,
          flexDirection: 'column',
          padding: '0 1rem',
          display: 'flex',
        }}
      >
      <div className="accepted-purchases-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem', flex: 1 }}>
        <div className={`ap-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}>
          <style>{`
            .ap-compact-bar-inner {
              max-width: 1100px;
              margin: 0 auto;
              width: 100%;
              box-sizing: border-box;
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 12px;
            }
            .ap-compact-title {
              font-family: Nunito, sans-serif;
              font-weight: 700;
              font-size: 1.125rem;
              color: #111827;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              min-width: 0;
              flex: 0 0 auto;
              text-align: left;
            }
            .ap-compact-spacer {
              flex: 1 1 auto;
              min-width: 0;
            }
            .ap-sticky-header .ap-link-btn {
              flex-shrink: 0;
            }
            .ap-sticky-header .ap-header-expanded {
              display: block;
            }
            .ap-sticky-header .ap-mobile-chrome-slot {
              display: none;
            }
            .ap-sticky-header .ap-compact-bar-mobile-fixed {
              display: none;
            }
            @media (min-width: 769px) {
              .ap-sticky-header .ap-compact-bar-mobile-fixed {
                display: none !important;
              }
              .ap-sticky-header .ap-compact-bar {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.7rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s ease;
              }
              .ap-sticky-header.is-compact .ap-compact-bar {
                opacity: 1;
                pointer-events: auto;
              }
              .ap-sticky-header .ap-link-btn {
                padding: 0.6rem 1.4rem !important;
              }
            }
            @media (max-width: 768px) {
              .accepted-purchases-page-content {
                padding-top: 0 !important;
              }
              .ap-sticky-header .ap-header-expanded {
                display: none !important;
              }
              .ap-sticky-header .ap-mobile-chrome-slot {
                display: block !important;
                position: relative;
                flex-shrink: 0;
                background-color: #F7F3EB;
                overflow: hidden;
                margin-left: -0.75rem;
                margin-right: -0.75rem;
                width: calc(100% + 1.5rem);
              }
              .ap-sticky-header .ap-compact-bar-inflow {
                display: block;
                padding: 0.6rem 1rem 1.5rem 1rem;
                background-color: #F7F3EB;
                box-sizing: border-box;
              }
              .ap-sticky-header .ap-compact-bar-inflow.is-suppressed {
                visibility: hidden;
                pointer-events: none;
              }
              .ap-sticky-header .ap-compact-bar-desktop {
                display: none !important;
              }
              .ap-sticky-header .ap-compact-bar-mobile-fixed {
                display: block;
              }
              .ap-sticky-header .ap-compact-bar {
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.85rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                pointer-events: none;
                transform: translateY(-100%);
                transition: transform 0.2s ease;
              }
              .ap-sticky-header .ap-compact-bar.is-mobile-visible {
                display: block;
                transform: translateY(0);
                pointer-events: auto;
              }
              .ap-sticky-header .ap-compact-title {
                font-size: calc(1rem + 1pt) !important;
                color: #5A9ECB !important;
              }
              .ap-sticky-header .ap-link-btn {
                padding: 0.5rem 1rem !important;
                font-size: 0.875rem !important;
              }
            }
          `}</style>

          <div
            ref={mobileChromeSlotRef}
            className="ap-mobile-chrome-slot"
            style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
          >
            <div
              ref={mobileChromeInflowRef}
              className={['ap-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
            >
              {renderMobileChromeBar()}
            </div>
          </div>

          <div
            className={[
              'ap-compact-bar',
              showMobileChromeBar && 'is-mobile-visible',
            ].filter(Boolean).join(' ')}
          >
            <div className="ap-compact-bar-mobile-fixed">
              {renderMobileChromeBar()}
            </div>
            <div className="ap-compact-bar-desktop">
              {renderMobileChromeBar()}
            </div>
          </div>

          <div className="ap-header-expanded">
            <Link
              to="/trading"
              className="accepted-purchases-header-back-link"
              style={{
                color: '#5A9ECB',
                textDecoration: 'none',
                alignItems: 'center',
                marginBottom: '1rem',
                fontSize: '1.125rem'
              }}
            >
              ← Back to Trading
            </Link>
            <div className="accepted-purchases-header-expanded-inner">
              <div className="accepted-purchases-header-copy">
                <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                  Accepted Purchases
                </h1>
                <p className="accepted-purchases-header-description text-gray-600" style={{ fontSize: '17px' }}>
                  {acceptedPurchases.length} purchase{acceptedPurchases.length !== 1 ? 's' : ''} • Sorted by most recent
                </p>
              </div>
              <div className="accepted-purchases-header-links">
                <Link to="/completed-purchases" className="ap-link-btn" style={headerLinkBtnStyle}>
                  Completed Purchases
                </Link>
              </div>
            </div>
          </div>
        </div>

      <style jsx>{`
        @media (min-width: 769px) {
          .accepted-purchases-page {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .accepted-purchases-header-back-link {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex !important;
            align-items: center;
          }
          .accepted-purchases-header-expanded-inner {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.5rem 5rem;
            margin-bottom: 1.5rem;
          }
          .accepted-purchases-header-copy {
            flex: 0 1 auto;
            min-width: 0;
          }
          .accepted-purchases-header-links {
            flex: 0 0 auto;
            margin-left: auto;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 0.5rem;
            align-self: flex-end;
          }
          .accepted-purchases-header-expanded-inner h1 {
            text-align: left !important;
            margin: 0 !important;
          }
          .accepted-purchases-header-description {
            margin: 0 !important;
            padding: 0 !important;
            text-align: left !important;
          }
          .accepted-purchases-list {
            max-width: 1100px !important;
            padding: 0 2rem !important;
            box-sizing: border-box !important;
            align-items: stretch !important;
          }
          .accepted-purchases-empty {
            padding: 0 2rem !important;
            box-sizing: border-box !important;
          }
          .accepted-purchase-card {
            width: 100% !important;
            max-width: none !important;
          }
        }
        @media (max-width: 768px) {
          .accepted-purchases-page {
            padding: 0 0.75rem !important;
          }
          .accepted-purchases-list {
            padding: 0 !important;
          }
          .accepted-purchases-empty {
            padding: 0 !important;
          }
        }
      `}</style>

        {acceptedPurchases.length === 0 ? (
          <div className="text-center py-8 accepted-purchases-empty" style={{ maxWidth: '1100px', margin: '0 auto', boxSizing: 'border-box' }}>
            <p className="text-gray-600">No accepted purchases found.</p>
          </div>
        ) : (
          <div className="accepted-purchases-list" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
            {acceptedPurchases.map((purchase) => {
              const isBuyer = currentUser?.uid === purchase.initiatorId;
              const otherUserId = isBuyer ? purchase.responderId : purchase.initiatorId;
              const otherUsername = usernames[otherUserId] || otherUserId?.slice(0, 6) || 'Unknown';
              const currentStep = getOverallPurchaseStatus(purchase);
              
              const getAcceptanceDate = (purchase) => {
                if (purchase.history && Array.isArray(purchase.history)) {
                  const acceptedEntry = [...purchase.history].reverse().find(h => 
                    h.message && h.message.toLowerCase().includes('accepted')
                  );
                  if (acceptedEntry && acceptedEntry.timestamp) {
                    return acceptedEntry.timestamp.toDate?.() || new Date(acceptedEntry.timestamp);
                  }
                }
                return purchase.createdAt?.toDate?.() || new Date();
              };
              
              const purchaseDate = getAcceptanceDate(purchase);
              const userPaid = isBuyer ? (purchase.paymentStatus?.buyerPaid || false) : (purchase.paymentStatus?.sellerPaid || false);
              const purchaseAmount = purchase.acceptedAmount || purchase.offerAmount || 0;

              return (
                <div 
                  key={purchase.id}
                  data-restore-anchor={purchase.id}
                  className="accepted-purchase-card"
                  style={{ 
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                    backgroundColor: 'white',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  <style jsx>{`
                    @media (max-width: 768px) {
                      .accepted-purchase-card {
                        width: 100% !important;
                        padding: 1rem !important;
                        margin: 0 0 1rem 0 !important;
                      }
                      .accepted-purchase-pedal-inner {
                        flex-direction: column !important;
                      }
                      .accepted-purchase-image {
                        width: 100% !important;
                        max-width: 180px !important;
                        height: auto !important;
                        aspect-ratio: 1 !important;
                      }
                      .accepted-purchase-buttons {
                        justify-content: space-between !important;
                        width: 100% !important;
                      }
                      .accepted-purchase-payment-button {
                        order: 3 !important;
                        width: 100% !important;
                        text-align: center !important;
                        box-sizing: border-box !important;
                      }
                      .accepted-purchase-header {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        gap: 0.5rem !important;
                      }
                      .accepted-purchase-header > div {
                        justify-content: flex-start !important;
                      }
                      .accepted-purchase-date-long {
                        display: none !important;
                      }
                      .accepted-purchase-date-short {
                        display: inline !important;
                      }
                    }
                  `}</style>

                  {/* Top row: Date + Status pills */}
                  <div className="accepted-purchase-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                    <span style={{ fontSize: '0.9rem', color: '#6b7280', fontFamily: 'Nunito', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      <span className="accepted-purchase-date-long">{purchaseDate ? purchaseDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Unknown date'}</span>
                      <span className="accepted-purchase-date-short" style={{ display: 'none' }}>{purchaseDate ? purchaseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date'}</span>
                    </span>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #C9A227', color: '#946F00', backgroundColor: 'rgba(201, 162, 39, 0.08)' }}>
                        ${typeof purchaseAmount === 'number' ? purchaseAmount.toFixed(0) : purchaseAmount} {isBuyer ? 'purchase' : 'sale'}
                      </span>
                      <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: userPaid ? '1px solid #a7c4a0' : '1px solid #fca5a5', color: userPaid ? '#5a7d53' : '#991b1b', backgroundColor: userPaid ? 'rgba(90, 125, 83, 0.07)' : 'rgba(153, 27, 27, 0.06)' }}>
                        {userPaid ? 'Paid' : 'Unpaid'}
                      </span>
                      <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #93c5fd', color: '#1e40af', backgroundColor: 'rgba(30, 64, 175, 0.06)' }}>
                        {currentStep}
                      </span>
                    </div>
                  </div>

                  {/* Pedal row */}
                  <div style={{ display: 'flex', alignItems: 'stretch', padding: '1rem 0' }}>
                    <div style={{ flex: 1 }}>
                      <div className="accepted-purchase-pedal-inner" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        {(purchase.targetPedal?.images?.top || placeholderFor(purchase.targetPedal || {}).url) ? (
                          <img
                            src={purchase.targetPedal?.images?.top || placeholderFor(purchase.targetPedal || {}).url}
                            alt='pedal'
                            className="accepted-purchase-image"
                            style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#f3f4f6', flexShrink: 0 }}
                          />
                        ) : (
                          <div className="accepted-purchase-image" style={{ width: '120px', height: '120px', backgroundColor: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>No Image</span>
                          </div>
                        )}
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                            {isBuyer ? `Buying from ${otherUsername}` : `Selling to ${otherUsername}`}
                          </div>
                          <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1f2937', fontFamily: 'Nunito' }}>{purchase.targetPedal?.make}</div>
                          <div style={{ fontSize: '0.85rem', color: '#6b7280', fontFamily: 'Nunito' }}>{purchase.targetPedal?.model}</div>
                          <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Nunito', marginTop: '2px' }}>
                            {purchase.targetPedal?.condition} · ${purchase.targetPedal?.price}
                          </div>
                          {purchase.targetPedalId && (
                            <Link
                              to={`/archived-listing/purchase/${purchase.id}/target`}
                              style={{ fontSize: '0.8rem', color: '#5A9ECB', textDecoration: 'none', fontFamily: 'Nunito', display: 'inline-block', marginTop: '4px' }}
                            >
                              View listing →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom section: Buttons */}
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
                    <div className="accepted-purchase-buttons" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Link
                        to={`/purchase-progress/${purchase.id}`}
                        style={{
                          padding: '8px 20px',
                          borderRadius: '9999px',
                          border: '1px solid #d1d5db',
                          backgroundColor: 'white',
                          color: '#374151',
                          fontWeight: '700',
                          fontSize: '0.9rem',
                          fontFamily: 'Nunito',
                          textDecoration: 'none',
                          display: 'inline-block',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        View progress
                      </Link>
                      {isBuyer && !userPaid && (
                        <Link
                          className="accepted-purchase-payment-button"
                          to={`/purchase-confirm/${purchase.id}`}
                          style={{
                            padding: '8px 20px',
                            borderRadius: '9999px',
                            border: '1px solid #C9A227',
                            backgroundColor: '#C9A227',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '0.9rem',
                            fontFamily: 'Nunito',
                            textDecoration: 'none',
                            display: 'inline-block',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Complete Payment →
                        </Link>
                      )}
                      {!isBuyer && !purchase.paymentStatus?.sellerPaid && (
                        <Link
                          className="accepted-purchase-payment-button"
                          to={`/seller-purchase-confirm/${purchase.id}`}
                          style={{
                            padding: '8px 20px',
                            borderRadius: '9999px',
                            border: '1px solid #C9A227',
                            backgroundColor: '#C9A227',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '0.9rem',
                            fontFamily: 'Nunito',
                            textDecoration: 'none',
                            display: 'inline-block',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Complete Payment →
                        </Link>
                      )}
                      <Link
                        to={`/purchase-messages/${purchase.id}`}
                        style={{
                          padding: '8px 20px',
                          borderRadius: '9999px',
                          border: '1px solid #d1d5db',
                          backgroundColor: 'white',
                          color: '#374151',
                          fontWeight: '700',
                          fontSize: '0.9rem',
                          fontFamily: 'Nunito',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Messages
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Fixed gap below content */}
        <div style={{ height: '200px' }}></div>
      </div>
      </div>
      
      {/* Footer */}
      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
    </div>
  );
};

export default AcceptedPurchases;
